import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import FormatBoldRoundedIcon from '@mui/icons-material/FormatBoldRounded';
import FormatItalicRoundedIcon from '@mui/icons-material/FormatItalicRounded';
import FormatStrikethroughRoundedIcon from '@mui/icons-material/FormatStrikethroughRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import DataObjectRoundedIcon from '@mui/icons-material/DataObjectRounded';
import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import FormatListNumberedRoundedIcon from '@mui/icons-material/FormatListNumberedRounded';
import HorizontalRuleRoundedIcon from '@mui/icons-material/HorizontalRuleRounded';
import InsertLinkRoundedIcon from '@mui/icons-material/InsertLinkRounded';
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import { Button, TextField, Chip, ThemeProvider, createTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';

// ─── MUI theme wired to app CSS variables ────────────────────────────────────
// Called inside the component so it re-reads variables on every render (theme switch).
function buildPickerTheme() {
  const s = getComputedStyle(document.documentElement);
  const v = (name) => s.getPropertyValue(name).trim();
  return createTheme({
    palette: {
      mode: v('--text').startsWith('#e') || v('--text').startsWith('#f') ? 'dark' : 'light',
      primary:    { main: v('--green') || '#e0895e' },
      background: { paper: v('--bg-2') || '#232120', default: v('--bg') || '#1b1a18' },
      text:       { primary: v('--text') || '#ece7dd', secondary: v('--text-2') || '#a49e93', disabled: v('--text-3') || '#726c62' },
      divider:    v('--border') || '#423e39',
      action:     { hover: v('--bg-3') || '#2b2926', selected: v('--green-glow') || 'rgba(224,137,94,0.15)' },
    },
    shape: { borderRadius: 6 },
    typography: { fontFamily: 'var(--font-tech), var(--font-sans), sans-serif', fontSize: 13 },
    components: {
      MuiPickersDay: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontFamily: 'var(--font-tech)',
            fontSize: '0.8rem',
            color: v('--text-2'),
            '&:hover':    { background: v('--bg-3') },
            '&.Mui-selected': {
              background: v('--green') + ' !important',
              color:      v('--btn-text') || '#201a15',
              fontWeight: 500,
            },
            '&.MuiPickersDay-today:not(.Mui-selected)': {
              border: `1px solid ${v('--green-border')}`,
            },
          },
        },
      },
      MuiPickersCalendarHeader: {
        styleOverrides: {
          label: { fontFamily: 'var(--font-tech)', fontSize: '0.85rem', fontWeight: 500, color: v('--text') },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          weekDayLabel: { fontFamily: 'var(--font-tech)', fontSize: '0.72rem', color: v('--text-3') },
        },
      },
      MuiPickersPopper: {
        styleOverrides: {
          paper: {
            background:   v('--bg-2'),
            border:       `1px solid ${v('--border')}`,
            borderRadius: 14,
            boxShadow:    '0 12px 34px -8px rgba(0,0,0,0.55)',
          },
        },
      },
      MuiYearCalendar: {
        styleOverrides: {
          root: { background: v('--bg-2') },
        },
      },
      MuiPickersYear: {
        styleOverrides: {
          yearButton: {
            fontFamily: 'var(--font-tech)',
            fontSize: '0.8rem',
            borderRadius: 6,
            color: v('--text-2'),
            '&.Mui-selected': { background: v('--green'), color: v('--btn-text') || '#201a15', fontWeight: 500 },
          },
        },
      },
    },
  });
}

// ─── Shared MUI sx tokens (mirrors Boards.js) ────────────────────────────────

// ─── Design tokens (6px radius · 34px height · weight 400) ───────────────────

const tfSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    background: 'var(--bg-3)',
    transition: 'box-shadow 0.18s ease',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-2)' },
    '&.Mui-focused': {
      boxShadow: '0 0 0 3px var(--green-glow)',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--green-border)', borderWidth: '1px' },
    },
  },
  '& .MuiInputBase-input': {
    color: 'var(--text)',
    fontFamily: 'var(--font-tech)',
    fontSize: '0.83rem',
    fontWeight: 400,
    padding: '7px 12px',
    '&::placeholder': { color: 'var(--text-3)', opacity: 1 },
  },
  '& .MuiInputLabel-root': {
    color: 'var(--text-3)',
    fontFamily: 'var(--font-tech)',
    fontSize: '0.83rem',
    fontWeight: 400,
    '&.Mui-focused': { color: 'var(--green)' },
  },
};

const tfSxSingle = {
  ...tfSx,
  '& .MuiInputBase-input': {
    ...tfSx['& .MuiInputBase-input'],
    padding: '9.5px 12px',
  },
};

const btnContainedSx = {
  borderRadius: '6px',
  height: '34px',
  fontFamily: 'var(--font-tech)',
  fontSize: '0.8rem',
  fontWeight: 400,
  textTransform: 'none',
  background: 'var(--green)',
  color: 'var(--btn-text)',
  boxShadow: 'none',
  px: 1.75,
  '&:hover': { background: 'var(--green)', opacity: 0.88, boxShadow: '0 0 0 3px var(--green-glow)', transform: 'translateY(-1px)' },
  '&.Mui-disabled': { background: 'var(--green)', color: 'var(--btn-text)', opacity: 0.45 },
};

const btnOutlinedSx = {
  borderRadius: '6px',
  height: '34px',
  fontFamily: 'var(--font-tech)',
  fontSize: '0.8rem',
  fontWeight: 400,
  textTransform: 'none',
  border: '1px solid var(--border)',
  color: 'var(--text-2)',
  boxShadow: 'none',
  px: 1.75,
  '&:hover': { background: 'var(--bg-3)', borderColor: 'var(--border-2)', color: 'var(--text)', boxShadow: 'none' },
};

const btnTextGreenSx = {
  borderRadius: '6px',
  height: '34px',
  fontFamily: 'var(--font-tech)',
  fontSize: '0.8rem',
  fontWeight: 400,
  textTransform: 'none',
  color: 'var(--green)',
  minWidth: 0,
  px: 0.75,
  '&:hover': { background: 'var(--bg-3)' },
};

const btnDangerSx = {
  borderRadius: '6px',
  height: '34px',
  fontFamily: 'var(--font-tech)',
  fontSize: '0.8rem',
  fontWeight: 400,
  textTransform: 'none',
  color: 'var(--text-3)',
  minWidth: 0,
  px: 0.75,
  '&:hover': { color: '#f87171', background: 'rgba(248,113,113,0.08)' },
};

const chipSx = (active) => ({
  fontFamily: 'var(--font-tech)',
  fontSize: '0.74rem',
  borderRadius: '6px',
  height: '28px',
  cursor: 'pointer',
  border: '1px solid',
  borderColor: active ? 'var(--green-border)' : 'var(--border)',
  color: active ? 'var(--green)' : 'var(--text-2)',
  background: active ? 'var(--green-glow)' : 'transparent',
  fontWeight: 400,
  '& .MuiChip-label': { px: 1.25 },
  '&:hover': { background: 'var(--green-glow)', borderColor: 'var(--green-border)', color: 'var(--green)' },
});

const API_BASE = (process.env.REACT_APP_API_BASE || 'https://backend.parthmerchant.com').replace(/\/$/, '');

const COVER_COLORS = [
  '#c47aab', '#8b2e50', '#6d5a9c', '#3a9970',
  '#1c2c5e', '#b06a4f', '#7a8b6f', '#4a4a44',
];

function coverColor(slug) {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) | 0;
  return COVER_COLORS[Math.abs(h) % COVER_COLORS.length];
}

function slugify(title) {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/[\s-]+/g, '-');
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ─── Markdown toolbar (no tab toggle — preview is always-on in right pane) ───

function MarkdownEditor({ content, onChange, onImageUpload }) {
  const textareaRef = useRef(null);

  const insert = useCallback((prefix, suffix = '', placeholder = 'text') => {
    const el = textareaRef.current;
    if (!el) return;
    const s = el.selectionStart, e = el.selectionEnd;
    const selected = el.value.slice(s, e) || placeholder;
    const newVal = el.value.slice(0, s) + prefix + selected + suffix + el.value.slice(e);
    onChange(newVal);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(s + prefix.length, s + prefix.length + selected.length);
    });
  }, [onChange]);

  const insertLine = useCallback((prefix) => {
    const el = textareaRef.current;
    if (!el) return;
    const s = el.selectionStart;
    const lineStart = el.value.lastIndexOf('\n', s - 1) + 1;
    const lineEnd = el.value.indexOf('\n', s);
    const line = el.value.slice(lineStart, lineEnd === -1 ? undefined : lineEnd);
    const before = el.value.slice(0, lineStart);
    const after = lineEnd === -1 ? '' : el.value.slice(lineEnd);
    const hasIt = line.startsWith(prefix);
    onChange(before + (hasIt ? line.slice(prefix.length) : prefix + line) + after);
    const cursor = lineStart + (hasIt ? 0 : prefix.length);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(cursor, cursor); });
  }, [onChange]);

  const insertBlock = useCallback((block) => {
    const el = textareaRef.current;
    if (!el) return;
    const s = el.selectionStart;
    const before = el.value.slice(0, s), after = el.value.slice(s);
    const pre = before === '' || before.endsWith('\n\n') ? '' : '\n\n';
    const suf = after === '' || after.startsWith('\n') ? '\n' : '\n\n';
    onChange(before + pre + block + suf + after);
    const pos = before.length + pre.length + block.length + 1;
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(pos, pos); });
  }, [onChange]);

  const ICN = { fontSize: 16 };
  const tools = [
    { label: 'H1', title: 'Heading 1', act: () => insertLine('# ') },
    { label: 'H2', title: 'Heading 2', act: () => insertLine('## ') },
    { label: 'H3', title: 'Heading 3', act: () => insertLine('### ') },
    { div: true },
    { icon: <FormatBoldRoundedIcon sx={ICN} />,            title: 'Bold',           act: () => insert('**', '**', 'bold') },
    { icon: <FormatItalicRoundedIcon sx={ICN} />,          title: 'Italic',         act: () => insert('_', '_', 'italic') },
    { icon: <FormatStrikethroughRoundedIcon sx={ICN} />,   title: 'Strikethrough',  act: () => insert('~~', '~~', 'strike') },
    { div: true },
    { icon: <CodeRoundedIcon sx={ICN} />,                  title: 'Inline code',    act: () => insert('`', '`', 'code') },
    { icon: <DataObjectRoundedIcon sx={ICN} />,            title: 'Code block',     act: () => insertBlock('```\ncode here\n```') },
    { icon: <FormatQuoteRoundedIcon sx={ICN} />,           title: 'Blockquote',     act: () => insertLine('> ') },
    { div: true },
    { icon: <FormatListBulletedRoundedIcon sx={ICN} />,    title: 'Bullet list',    act: () => insertLine('- ') },
    { icon: <FormatListNumberedRoundedIcon sx={ICN} />,    title: 'Numbered list',  act: () => insertLine('1. ') },
    { icon: <HorizontalRuleRoundedIcon sx={ICN} />,        title: 'Horizontal rule',act: () => insertBlock('---') },
    { div: true },
    { icon: <InsertLinkRoundedIcon sx={ICN} />,            title: 'Link',           act: () => insert('[', '](url)', 'link text') },
    { icon: <AddPhotoAlternateRoundedIcon sx={ICN} />,     title: 'Upload image',   act: onImageUpload },
  ];

  return (
    <div className="writing-md-editor">
      <div className="writing-md-toolbar">
        {tools.map((t, i) =>
          t.div
            ? <span key={i} className="md-toolbar-divider" />
            : (
              <button
                key={i}
                type="button"
                className={`md-toolbar-btn${t.icon ? ' md-toolbar-btn--icon' : ' md-toolbar-btn--text'}`}
                title={t.title}
                onClick={t.act}
              >
                {t.icon ?? t.label}
              </button>
            )
        )}
      </div>
      <textarea
        ref={textareaRef}
        className="writing-md-textarea"
        value={content}
        onChange={e => onChange(e.target.value)}
        placeholder="Write your post in Markdown…"
        spellCheck
      />
    </div>
  );
}

// ─── Full post preview (mirrors BlogPost render) ──────────────────────────────

function PostPreview({ meta, content }) {
  const tags = meta.tags
    ? meta.tags.split(',').map(t => t.trim()).filter(Boolean)
    : [];

  return (
    <div className="writing-preview-inner">
      {meta.image ? (
        <img className="post-hero-img" src={meta.image} alt={meta.title || ''} />
      ) : (
        <div className="writing-preview-cover-placeholder" />
      )}
      {tags.length > 0 && (
        <div className="post-tags" style={{ marginTop: '1rem' }}>
          {tags.map(t => <span key={t} className="tag">#{t}</span>)}
        </div>
      )}
      {meta.title
        ? <h1 className="post-page-title">{meta.title}</h1>
        : <h1 className="post-page-title" style={{ color: 'var(--text-3)' }}>Untitled post…</h1>
      }
      {meta.date && (
        <p className="post-date">
          {new Date(meta.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      )}
      <div className="post-body">
        {content
          ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          : <p style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>Start writing to see a live preview…</p>
        }
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const BLANK_META = {
  slug: '', title: '', date: todayISO(),
  excerpt: '', image: '', tags: '', published: true,
};

export default function AdminWriting() {
  const { token } = useAuth();
  const [view, setView]               = useState('list');
  const [posts, setPosts]             = useState([]);
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [toast, setToast]             = useState(null);

  // Editor state
  const [isNew, setIsNew]             = useState(false);
  const [editSlug, setEditSlug]       = useState('');
  const [meta, setMeta]               = useState(BLANK_META);
  const [content, setContent]         = useState('');
  const [slugEdited, setSlugEdited]   = useState(false);

  const fileInputRef = useRef(null);

  const showToast = useCallback((msg, type = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  }, []);

  const Toast = toast ? (
    <div className={`boards-toast boards-toast-${toast.type}`}>{toast.msg}</div>
  ) : null;

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
  }), [token]);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/posts/all`, {
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setPosts(await res.json());
    } catch {
      showToast('Could not load posts', 'error');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, showToast]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const openNewEditor = () => {
    setIsNew(true);
    setEditSlug('');
    setMeta(BLANK_META);
    setContent('');
    setSlugEdited(false);
    setView('editor');
  };

  const openEditEditor = async (post) => {
    try {
      const res = await fetch(`${API_BASE}/api/posts/${post.slug}/edit`, {
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const full = await res.json();
      setIsNew(false);
      setEditSlug(full.slug);
      setMeta({
        slug:      full.slug,
        title:     full.title,
        date:      full.date,
        excerpt:   full.excerpt || '',
        image:     full.image || '',
        tags:      (full.tags || []).join(', '),
        published: full.published,
      });
      setContent(full.content || '');
      setSlugEdited(true);
      setView('editor');
    } catch {
      showToast('Could not load post', 'error');
    }
  };

  const handleTitleChange = (title) => {
    setMeta(m => ({ ...m, title, slug: slugEdited ? m.slug : slugify(title) }));
  };

  const handleSave = async (publishedState) => {
    if (!meta.title.trim()) { showToast('Title is required', 'error'); return; }
    if (!meta.slug.trim())  { showToast('Slug is required', 'error'); return; }
    setSaving(true);
    const body = {
      title:     meta.title.trim(),
      date:      meta.date,
      excerpt:   meta.excerpt.trim(),
      image:     meta.image.trim() || null,
      tags:      meta.tags.split(',').map(t => t.trim()).filter(Boolean),
      content,
      published: publishedState,
    };
    try {
      let res;
      if (isNew) {
        res = await fetch(`${API_BASE}/api/posts/`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...body, slug: meta.slug.trim() }),
        });
      } else {
        res = await fetch(`${API_BASE}/api/posts/${editSlug}`, {
          method: 'PUT',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `${res.status}` }));
        throw new Error(err.detail || `${res.status}`);
      }
      const saved = await res.json();
      if (isNew) { setIsNew(false); setEditSlug(saved.slug); setSlugEdited(true); }
      setMeta(m => ({ ...m, published: saved.published }));
      showToast(publishedState ? 'Post published' : 'Saved as draft', 'success');
      loadPosts();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${meta.title}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/posts/${editSlug}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      showToast('Post deleted', 'success');
      loadPosts();
      setView('list');
    } catch {
      showToast('Could not delete post', 'error');
      setSaving(false);
    }
  };

  const handleDeleteFromList = async (e, post) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${post.title}"?`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/posts/${post.slug}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      showToast('Post deleted', 'success');
      loadPosts();
    } catch {
      showToast('Could not delete post', 'error');
    }
  };

  const handleImageUpload = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await fetch(`${API_BASE}/api/posts/img`, {
        method: 'POST',
        headers: authHeaders(),
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Upload failed' }));
        throw new Error(err.detail || 'Upload failed');
      }
      const { image_id } = await res.json();
      const imageUrl = `${API_BASE}/api/posts/img/${image_id}`;
      const altText = file.name.replace(/\.[^.]+$/, '');
      setContent(prev => `${prev}\n\n![${altText}](${imageUrl})\n`);
      showToast('Image inserted', 'success');
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const filteredPosts = posts.filter(p =>
    filterStatus === 'published' ? p.published :
    filterStatus === 'draft'     ? !p.published :
    true
  );

  // ─── Editor view ─────────────────────────────────────────────────────────────
  if (view === 'editor') {
    return (
      <div className="writing-editor-page">
        {Toast}

        {/* Top bar */}
        <div className="writing-editor-topbar">
          <Button
            startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={() => setView('list')}
            sx={{ ...btnOutlinedSx, px: 1.25 }}
          >
            All Posts
          </Button>
          <div className="writing-editor-actions">
            {!isNew && (
              <Button
                startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />}
                onClick={handleDelete}
                disabled={saving}
                sx={btnDangerSx}
              >
                Delete
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => handleSave(false)}
              disabled={saving}
              sx={btnOutlinedSx}
            >
              {saving ? 'Saving…' : 'Save Draft'}
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={() => handleSave(true)}
              disabled={saving}
              sx={btnContainedSx}
            >
              {saving ? 'Saving…' : meta.published ? 'Update' : 'Publish'}
            </Button>
          </div>
        </div>

        {/* Split body */}
        <div className="writing-editor-body">

          {/* ── Left: metadata + editor ── */}
          <div className="writing-editor-left">
            <div className="writing-editor-meta">
              {/* Title — large, ghost input */}
              <input
                className="writing-title-input"
                placeholder="Post title…"
                value={meta.title}
                onChange={e => handleTitleChange(e.target.value)}
              />

              {/* Fields grid */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div className="writing-meta-fields">
                <div className="writing-meta-field">
                  <TextField
                    label="Slug"
                    size="small"
                    fullWidth
                    value={meta.slug}
                    onChange={e => { setSlugEdited(true); setMeta(m => ({ ...m, slug: e.target.value })); }}
                    placeholder="post-slug"
                    disabled={!isNew}
                    title={!isNew ? 'Slug cannot be changed after creation' : undefined}
                    sx={tfSxSingle}
                  />
                </div>
                <div className="writing-meta-field">
                  <ThemeProvider theme={buildPickerTheme()}>
                  <DatePicker
                    label="Date"
                    value={meta.date ? dayjs(meta.date) : null}
                    onChange={v => setMeta(m => ({ ...m, date: v ? v.format('YYYY-MM-DD') : '' }))}
                    slotProps={{
                      textField: { size: 'small', fullWidth: true, sx: tfSxSingle },
                      openPickerButton: { size: 'small', sx: { color: 'var(--text-3)', '&:hover': { color: 'var(--green)', background: 'var(--bg-3)' } } },
                    }}
                  />
                  </ThemeProvider>
                </div>
                <div className="writing-meta-field writing-meta-field--full">
                  <TextField
                    label="Tags"
                    size="small"
                    fullWidth
                    value={meta.tags}
                    onChange={e => setMeta(m => ({ ...m, tags: e.target.value }))}
                    placeholder="engineering, react, design"
                    sx={tfSxSingle}
                  />
                </div>
                <div className="writing-meta-field writing-meta-field--full">
                  <TextField
                    label="Excerpt"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    value={meta.excerpt}
                    onChange={e => setMeta(m => ({ ...m, excerpt: e.target.value }))}
                    placeholder="A short description shown in post cards and listings…"
                    sx={tfSx}
                  />
                </div>
                <div className="writing-meta-field writing-meta-field--full">
                  <TextField
                    label="Cover Image URL"
                    size="small"
                    fullWidth
                    value={meta.image}
                    onChange={e => setMeta(m => ({ ...m, image: e.target.value }))}
                    placeholder="https://…"
                    sx={tfSxSingle}
                  />
                </div>
              </div>
              </LocalizationProvider>
            </div>

            <MarkdownEditor
              content={content}
              onChange={setContent}
              onImageUpload={handleImageUpload}
            />
          </div>

          {/* ── Right: live preview ── */}
          <div className="writing-editor-right">
            <div className="writing-preview-label">Preview</div>
            <PostPreview meta={meta} content={content} />
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>
    );
  }

  // ─── List view ────────────────────────────────────────────────────────────────
  return (
    <div className="boards-page">
      {Toast}

      <div className="boards-list-header">
        <div>
          <span className="section-label">Admin</span>
          <h2 className="boards-list-title">Writing</h2>
        </div>
        <Button
          startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={openNewEditor}
          sx={btnTextGreenSx}
        >
          New Post
        </Button>
      </div>

      {/* Status filter */}
      <div className="writing-filter">
        {[
          { id: 'all',       label: 'All' },
          { id: 'published', label: 'Published' },
          { id: 'draft',     label: 'Drafts' },
        ].map(f => (
          <Chip
            key={f.id}
            label={f.label}
            variant="outlined"
            size="small"
            onClick={() => setFilterStatus(f.id)}
            sx={chipSx(filterStatus === f.id)}
          />
        ))}
        <span className="writing-filter-count">
          {filteredPosts.length} post{filteredPosts.length !== 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <div className="boards-empty">Loading posts…</div>
      ) : filteredPosts.length === 0 ? (
        <div className="boards-empty">
          <p>No posts yet.</p>
          <Button
            variant="contained"
            disableElevation
            startIcon={<AddRoundedIcon sx={{ fontSize: 14 }} />}
            onClick={openNewEditor}
            sx={btnContainedSx}
          >
            Write your first one
          </Button>
        </div>
      ) : (
        <div className="boards-grid">
          {filteredPosts.map(post => (
            <button
              key={post.slug}
              type="button"
              className="writing-card"
              onClick={() => openEditEditor(post)}
            >
              {/* Cover image / color band */}
              <div
                className="writing-card-cover"
                style={{ background: coverColor(post.slug) }}
              >
                {post.image && (
                  <img src={post.image} alt="" className="writing-card-img" loading="lazy" />
                )}
                <span className={`writing-card-status ${post.published ? 'published' : 'draft'}`}>
                  {post.published ? 'Published' : 'Draft'}
                </span>
                <button
                  type="button"
                  className="writing-card-delete"
                  onClick={e => handleDeleteFromList(e, post)}
                  title="Delete post"
                >
                  ×
                </button>
              </div>

              {/* Card body */}
              <div className="boards-card-body">
                <span className="boards-card-name">{post.title}</span>
                {post.excerpt && (
                  <span className="boards-card-desc">{post.excerpt}</span>
                )}
                <span className="boards-card-count">
                  {new Date(post.date).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric',
                  })}
                  {post.tags?.length > 0 && ` · ${post.tags.slice(0, 2).join(', ')}`}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
